import React, { useState, useEffect } from 'react';
import { Card, CardContent, InputBase, Select, MenuItem, Chip } from '@mui/material';
import { H4, H1, H2, H3 } from '../../components/typography';
import Button from '../../components/button';
import Spacer from '../../components/Spacer';
import SkeletonContainer from '../../components/skeletonContainer';
import { Eye, Check, X, Clock, BadgeCheck, Ban, MoreHorizontal, AlertCircle, Info, MessageSquare } from 'lucide-react';
import AttachmentsSection from '../../components/AttachmentsSection';
import { Dialog } from '@headlessui/react';
import { useToast } from '../../components/ui/ToastProvider';
import { auth, db, storage, functions } from '../../config/firebase';
import { resolveBoardOwnerName } from '../../utils/applicationOwner';
import { collection, doc, getDoc, getDocs, query, where, updateDoc } from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import { onAuthStateChanged } from 'firebase/auth';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import ChatPanel from '../../components/chat/ChatPanel';
import ChatToggleButton from '../../components/chat/ChatToggleButton';
import { formatMoment } from '../../utils/dates';
import { classifyApplication, isCurrentRental, rentalPhaseLabel, summarizeApplications } from '../../utils/applicationStats';

const statusOptions = ['All statuses', 'Pending', 'Approved', 'Active', 'Rejected'];
const dateOptions = ['All time', 'Next 30 days', 'Next 90 days'];

// Helper function to get unified status badge
const getApplicationStatusBadge = (app) => {
  // Priority 0: Extension request pending (overrides all other statuses)
  if (app.extensionRequest?.status === 'pending') {
    return (
      <Chip
        label="Förlängningsförfrågan"
        size="small"
        sx={{
          backgroundColor: '#FFF9C4',
          color: '#F57F17',
          fontWeight: 600,
          fontSize: '0.75rem',
        }}
      />
    );
  }

  // Priority 1: Fasen för en uthyrning — ur datumen, inte ur rentalStatus
  // (nattjobbets fält som aldrig rör status). En godkänd uthyrning som inte
  // börjat är "Godkänd, startar …", inte "Aktiv".
  const phaseLabel = rentalPhaseLabel(app);
  if (phaseLabel) {
    const palette = {
      green: { backgroundColor: '#E8F5E9', color: '#4CAF50' },
      blue: { backgroundColor: '#E3F2FD', color: '#2196F3' },
      amber: { backgroundColor: '#FFF3E0', color: '#FF9800' },
      gray: { backgroundColor: '#F5F5F5', color: '#9E9E9E' },
    }[phaseLabel.tone];
    return (
      <Chip
        label={phaseLabel.text}
        size="small"
        sx={{ ...palette, fontWeight: 600, fontSize: '0.75rem' }}
      />
    );
  }

  // Priority 2: Application status
  if (app.status === 'CREDIT_CHECK_COMPLETE') {
    return (
      <Chip
        label="Kreditcheck klar"
        size="small"
        sx={{
          backgroundColor: '#F3E5F5',
          color: '#9C27B0',
          fontWeight: 600,
          fontSize: '0.75rem',
        }}
      />
    );
  }

  if (app.status === 'TENANT_INFO_SUBMITTED') {
    if (app.creditCheck?.status === 'consent_pending') {
      return (
        <Chip
          label="⏳ Väntar på samtycke"
          size="small"
          sx={{
            backgroundColor: '#FFF9C4',
            color: '#F57F17',
            fontWeight: 600,
            fontSize: '0.75rem',
          }}
        />
      );
    }
    if (app.creditCheck?.status === 'consent_given') {
      return (
        <Chip
          label="✅ Samtycke mottaget"
          size="small"
          sx={{
            backgroundColor: '#E8F5E9',
            color: '#4CAF50',
            fontWeight: 600,
            fontSize: '0.75rem',
          }}
        />
      );
    }
    return (
      <Chip
        label="📋 Behöver kreditkontroll"
        size="small"
        sx={{
          backgroundColor: '#E3F2FD',
          color: '#2196F3',
          fontWeight: 600,
          fontSize: '0.75rem',
        }}
      />
    );
  }

  if (app.status === 'PENDING' || app.status === 'ADDITIONAL_INFO') {
    return (
      <Chip
        label="Pågående"
        size="small"
        sx={{
          backgroundColor: '#FFF9C4',
          color: '#F57F17',
          fontWeight: 600,
          fontSize: '0.75rem',
        }}
      />
    );
  }

  if (app.status === 'APPROVED') {
    return (
      <Chip
        label="Godkänd"
        size="small"
        sx={{
          backgroundColor: '#E8F5E9',
          color: '#4CAF50',
          fontWeight: 600,
          fontSize: '0.75rem',
        }}
      />
    );
  }

  if (app.status === 'ACTIVE' || app.status === 'FINAL_APPROVED') {
    return (
      <Chip
        label="Aktiv"
        size="small"
        sx={{
          backgroundColor: '#E3F2FD',
          color: '#2196F3',
          fontWeight: 600,
          fontSize: '0.75rem',
        }}
      />
    );
  }

  if (app.status === 'AWAITING_OWNER_DECISION') {
    return (
      <Chip
        label="Väntar på ägare"
        size="small"
        sx={{
          backgroundColor: '#FFF3E0',
          color: '#F57C00',
          fontWeight: 600,
          fontSize: '0.75rem',
        }}
      />
    );
  }

  if (app.status === 'OWNER_REJECTED') {
    return (
      <Chip
        label="Avböjd av ägare"
        size="small"
        sx={{
          backgroundColor: '#FFEBEE',
          color: '#C62828',
          fontWeight: 600,
          fontSize: '0.75rem',
        }}
      />
    );
  }

  if (app.status === 'REJECTED' || app.status === 'FINAL_REJECTED') {
    return (
      <Chip
        label="Avvisad"
        size="small"
        sx={{
          backgroundColor: '#FFEBEE',
          color: '#C62828',
          fontWeight: 600,
          fontSize: '0.75rem',
        }}
      />
    );
  }

  // Fallback
  return (
    <Chip
      label={app.status}
      size="small"
      sx={{
        backgroundColor: '#F5F5F5',
        color: '#9E9E9E',
        fontWeight: 600,
        fontSize: '0.75rem',
      }}
    />
  );
};

export default function Applications() {
  const { t } = useTranslation();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Check for URL filter parameter (legacy support)
  const urlFilter = searchParams.get('filter');

  // Set initial tab based on location state (från Overview KPI) eller URL filter (legacy)
  const getInitialTab = () => {
    // Först, kolla om vi har location.state från Overview KPI-klick
    if (location.state?.initialTab) {
      return location.state.initialTab;
    }

    // Annars, kolla URL-filter (legacy support)
    switch(urlFilter) {
      case 'active': return 'Active';
      case 'pending': return 'Pending';
      case 'ongoing': return 'Pending'; // Ongoing applications are often pending
      case 'expiring': return 'Active'; // Expiring contracts are active ones
      case 'extension': return 'Extension';
      default: return 'All'; // Visa alla ansökningar som default
    }
  };
  
  // Set initial status based on URL filter
  const getInitialStatus = () => {
    switch(urlFilter) {
      case 'active': return 'ACTIVE';
      case 'pending': return 'PENDING';
      case 'ongoing': return 'PENDING';
      case 'expiring': return 'ACTIVE';
      case 'credit_check_complete': return 'CREDIT_CHECK_COMPLETE';
      default: return 'all';
    }
  };
  
  const [tab, setTab] = useState(getInitialTab());
  const [showArchived, setShowArchived] = useState(false);
  const [status, setStatus] = useState(getInitialStatus());
  const [owner, setOwner] = useState(t('dashboard.applications.filters.owner.any'));
  const [date, setDate] = useState(t('dashboard.applications.filters.date.all'));
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [applications, setApplications] = useState([]);
  const [loadingId, setLoadingId] = useState(null);
  const toast = useToast();
  const [error, setError] = useState(null);
  const [finalRejectionReason, setFinalRejectionReason] = useState('');
  const [finalDecisionLoading, setFinalDecisionLoading] = useState(false);
  const [rejectionReason, setRejectionReason] = useState(''); // För första avslagsflödet
  const [showRejectionInput, setShowRejectionInput] = useState(false); // Visa kommentarsfält
  const [pdfDownloadLoading, setPdfDownloadLoading] = useState(false);
  const [ownerNames, setOwnerNames] = useState({}); // Nytt state för att lagra namn för ownerUid
  const [showMoreDetails, setShowMoreDetails] = useState(false); // För expanderbar sektion
  const [extensionLoading, setExtensionLoading] = useState(false); // För förlängningshantering
  const [extensionRejectionReason, setExtensionRejectionReason] = useState(''); // För avslag av förlängning
  const [showExtensionRejectionInput, setShowExtensionRejectionInput] = useState(false); // Visa avslag-fält för förlängning
  const [modalTab, setModalTab] = useState('details');
  const [currentUserName, setCurrentUserName] = useState('');

  const ownerOptions = [t('dashboard.applications.filters.owner.any'), ...Array.from(new Set(applications.map(a => a.owner)))];

  // Update filters when URL changes
  useEffect(() => {
    const urlFilter = searchParams.get('filter');
    if (urlFilter) {
      switch(urlFilter) {
        case 'active':
          setTab('Active');
          setStatus('ACTIVE');
          break;
        case 'pending':
          setTab('Pending');
          setStatus('PENDING');
          break;
        case 'ongoing':
          setTab('Pending');
          setStatus('PENDING');
          break;
        case 'expiring':
          setTab('Active');
          setStatus('ACTIVE');
          setDate('Next 30 days'); // For expiring contracts
          break;
        case 'credit_check_complete':
          setTab('CreditCheckComplete');
          setStatus('CREDIT_CHECK_COMPLETE');
          break;
        case 'extension':
          // Statusfiltret lämnas på "alla": en förlängning kan ligga på en
          // ansökan i vilken aktiv status som helst, och skulle annars
          // filtreras bort.
          setTab('Extension');
          setStatus('all');
          break;
        default:
          break;
      }
    }
  }, [searchParams]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        fetchApplicationsForUser(user.uid);
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const data = userDoc.data();
            setCurrentUserName(`${data.firstName || ''} ${data.lastName || ''}`.trim());
          }
        } catch (e) {}
      } else {
        setError(t('dashboard.applications.messages.noUser'));
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  async function fetchApplicationsForUser(uid) {
    setLoading(true);
    setError(null);
    try {
      const userRef = doc(db, 'users', uid);
      const userSnap = await getDoc(userRef);
      if (!userSnap.exists()) throw new Error(t('dashboard.applications.messages.noUserInfo'));
      
      const userData = userSnap.data();
      const associationId = userData.associationId;
      const housingCooperative = userData.housingCooperative;
      
      if (!associationId) throw new Error(t('dashboard.applications.messages.noBRF'));
      
      const q = query(
        collection(db, 'applications'),
        where('associationId', '==', associationId)
      );
      const querySnapshot = await getDocs(q);

      // Föreningens lägenheter — medlemsregistret svarar när ansökan saknar ägarnamn.
      let housingData = [];
      try {
        const housingSnap = await getDocs(
          query(collection(db, 'housing'), where('associationId', '==', associationId))
        );
        housingData = housingSnap.docs.map((h) => ({ id: h.id, ...h.data() }));
      } catch (housingError) {
        console.warn('Kunde inte läsa medlemsregistret för ägarnamn:', housingError);
      }
      
      const apps = [];
      for (const docSnapshot of querySnapshot.docs) {
        const data = docSnapshot.data();
        
        // En definition av ägarnamnet, delad med Hyria-admins listor. Förut byggdes
        // namnet här och den sparade platshållaren "Okänd Ägare" togs för ett namn.
        const ownerName = await resolveBoardOwnerName({
          application: data,
          housingList: housingData,
          fetchUser: async (uid) => {
            const userDoc = await getDoc(doc(db, 'users', uid));
            return userDoc.exists() ? userDoc.data() : null;
          },
        });

        apps.push({
          id: docSnapshot.id,
          owner: ownerName,
          apt: data.apartmentNumber,
          tenant: `${data.tenantFirstName || ''} ${data.tenantLastName || ''}`.trim(),
          start: data.fromDate,
          end: data.toDate,
          status: data.status,
          ownerUid: data.ownedBy || '',
          ...data
        });
      }
      
      setApplications(apps);
      // Hämta namn för alla unika ownerUid (om du vill visa namn istället för mail)
      const ownerUids = [...new Set(apps.map(a => a.ownerUid).filter(Boolean))];
      const ownerNameMap = {};
      for (const ownerUid of ownerUids) {
        try {
          const ownerRef = doc(db, 'users', ownerUid);
          const ownerSnap = await getDoc(ownerRef);
          if (ownerSnap.exists()) {
            const owner = ownerSnap.data();
            ownerNameMap[ownerUid] = `${owner.firstName || ''} ${owner.lastName || ''}`.trim();
          } else {
            ownerNameMap[ownerUid] = 'Okänd ägare';
          }
        } catch (err) {
          ownerNameMap[ownerUid] = 'Okänd ägare';
        }
      }
      setOwnerNames(ownerNameMap);
      // console.log('Hittade ansökningar:', apps);
    } catch (err) {
      setError(err.message || 'Något gick fel vid hämtning av ansökningar');
    } finally {
      setLoading(false);
    }
  }

  // KPI counts
  // Arkiverade ansökningar (medlem borttagen) döljs som standard.
  const visibleApplications = showArchived ? applications : applications.filter(a => !a.archivedAt);

  // En källa för räkningarna (utils/applicationStats) — samma som översikten.
  const stats = summarizeApplications(visibleApplications);
  const kpi = {
    Pending: stats.pending,
    Approved: stats.awaitingTenant,
    // Godkända/Aktiva = uthyrningar som upptar lägenheten eller är godkända
    // men inte börjat. Inte APPROVED utan hyresgäst, inte passerade perioder.
    Active: stats.current,
    CreditCheckComplete: stats.creditCheck,
    Rejected: stats.rejected,
    Historical: stats.ended,
    // Förlängningar är inte en status utan ett fält på ansökan — de kan ligga
    // i vilken aktiv uthyrning som helst och gick därför inte att filtrera på.
    Extension: visibleApplications.filter(a => a.extensionRequest?.status === 'pending').length,
  };

  // Uthyrningsportföljen — ur avtalsdatumen, inte ur nattjobbets rentalStatus.
  const rentalKpi = {
    APPROVED: stats.upcoming,
    ACTIVE: stats.active + stats.terminating,
    EXPIRING_SOON: stats.expiringSoon,
    COMPLETED: stats.ended,
  };

  // Filtered applications
  let filtered = visibleApplications;
  if (tab === 'Pending') filtered = filtered.filter(a => a.status?.toLowerCase() === 'pending' || a.status?.toLowerCase() === 'additional_info');
  if (tab === 'Active') filtered = filtered.filter(a => isCurrentRental(a));
  if (tab === 'CreditCheckComplete') filtered = filtered.filter(a => a.status?.toLowerCase() === 'credit_check_complete' || a.status?.toLowerCase() === 'tenant_info_submitted');
  if (tab === 'Rejected') filtered = filtered.filter(a => a.status?.toLowerCase() === 'rejected' || a.status?.toLowerCase() === 'final_rejected');
  if (tab === 'Historical') filtered = filtered.filter(a => classifyApplication(a) === 'ended');
  if (tab === 'Extension') filtered = filtered.filter(a => a.extensionRequest?.status === 'pending');
  if (status !== 'all') {
    if (status === 'pending') filtered = filtered.filter(a => a.status?.toLowerCase() === 'pending' || a.status?.toLowerCase() === 'additional_info');
    else if (status === 'credit_check_complete') filtered = filtered.filter(a => a.status?.toLowerCase() === 'credit_check_complete');
    else filtered = filtered.filter(a => a.status?.toLowerCase() === status.toLowerCase());
  }
  if (owner !== t('dashboard.applications.filters.owner.any')) filtered = filtered.filter(a => a.owner === owner);
  // Date filter mock (not implemented)

  const handleStatusChange = async (id, status, reason = '') => {
    status = status.toUpperCase();
    setLoadingId(id + status);
    try {
      // Uppdatera i Firestore
      const appRef = doc(db, 'applications', id);
      const updateData = {
        status,
        reviewedBy: auth.currentUser?.email || '',
        reviewedByName: currentUserName || '',
        reviewedAt: new Date().toISOString(),
      };
      
      // Lägg till kommentar om det är ett avslag
      if (status === 'REJECTED' && reason) {
        updateData.rejectionReason = reason;
      }
      
      await updateDoc(appRef, updateData);
      // Uppdatera lokal state
      setApplications((prev) => prev.map(app => app.id === id ? { ...app, ...updateData } : app));
      let toastMsg = t('dashboard.applications.messages.statusUpdated');
      if (status === 'APPROVED' || status === 'ACTIVE') toastMsg = t('dashboard.applications.messages.applicationApproved');
      else if (status === 'REJECTED') toastMsg = t('dashboard.applications.messages.applicationRejected');
      toast({
        type: (status === 'APPROVED' || status === 'ACTIVE') ? 'success' : (status === 'REJECTED' ? 'error' : 'default'),
        message: toastMsg
      });
      
      // Stäng modalen efter lyckad uppdatering
      setModalOpen(false);
      setSelectedApp(null);
      setRejectionReason(''); // Rensa kommentar-fältet
      setShowRejectionInput(false); // Dölj kommentarsfält
    } catch (err) {
      toast({ type: 'error', message: t('dashboard.applications.messages.updateError') });
    } finally {
      setLoadingId(null);
    }
  };

  const handleFinalDecision = async (id, status, reason = '') => {
    setFinalDecisionLoading(true);
    try {
      const appRef = doc(db, 'applications', id);

      // Bygg updateData-objekt
      const updateData = {
        status,
        finalApprovedAt: new Date().toISOString(),
        finalApprovedBy: auth.currentUser?.email || '',
        finalDecisionByName: currentUserName || '',
      };

      // Lägg till creditCheck.status vid godkännande/avslag
      if (status === 'AWAITING_OWNER_DECISION') {
        updateData['creditCheck.status'] = 'approved';
        updateData['creditCheck.approvedAt'] = new Date().toISOString();
      } else if (status === 'FINAL_REJECTED') {
        updateData.finalRejectionReason = reason;
        updateData['creditCheck.status'] = 'rejected';
        updateData['creditCheck.rejectedAt'] = new Date().toISOString();
      }

      await updateDoc(appRef, updateData);
      setApplications((prev) => prev.map(app => app.id === id ? { ...app, ...updateData, finalRejectionReason: reason } : app));
      setModalOpen(false);
      setSelectedApp(null);
      setFinalRejectionReason('');
    } catch (err) {
      toast({ type: 'error', message: 'Kunde inte uppdatera status.' });
    } finally {
      setFinalDecisionLoading(false);
    }
  };

  const handlePdfDownload = async (pdfUrl) => {
    setPdfDownloadLoading(true);
    try {
      // console.log('🔍 Attempting to download PDF:', pdfUrl);

      if (!pdfUrl) {
        toast({ type: 'error', message: 'Ingen PDF tillgänglig' });
        return;
      }

      // Om det är en Google Cloud Storage URL (gs://), konvertera till nedladdningsbar URL
      if (pdfUrl.startsWith('gs://')) {
        // console.log('📦 Processing Google Storage URL');
        const path = pdfUrl.replace('gs://hyria-storage/', '');
        // console.log('📂 Storage path:', path);

        const storageRef = ref(storage, path);
        // console.log('🔗 Storage ref created, getting download URL...');

        const downloadUrl = await getDownloadURL(storageRef);
        // console.log('✅ Download URL retrieved:', downloadUrl);

        // Öppna PDF i ny flik
        window.open(downloadUrl, '_blank');
        toast({ type: 'success', message: 'PDF öppnas i ny flik' });
      } else if (pdfUrl.startsWith('https://')) {
        // console.log('🌐 Processing HTTPS URL');
        // Om det redan är en HTTPS URL, öppna direkt
        window.open(pdfUrl, '_blank');
        toast({ type: 'success', message: 'PDF öppnas i ny flik' });
      } else {
        toast({ type: 'error', message: 'Ogiltig PDF-URL format' });
      }
    } catch (error) {

      let errorMessage = 'Kunde inte ladda ner PDF';
      if (error.code === 'storage/object-not-found') {
        errorMessage = 'PDF-filen hittades inte i systemet';
      } else if (error.code === 'storage/unauthorized') {
        errorMessage = 'Du har inte behörighet att ladda ner denna fil';
      } else if (error.code === 'storage/quota-exceeded') {
        errorMessage = 'Kvoten för nedladdningar är överskriden';
      }

      toast({ type: 'error', message: errorMessage });
    } finally {
      setPdfDownloadLoading(false);
    }
  };

  // Hantera godkännande av förlängningsförfrågan
  const handleApproveExtension = async (id) => {
    setExtensionLoading(true);
    try {
      const approveExtension = httpsCallable(functions, 'approveExtensionRequest');
      await approveExtension({ applicationId: id });

      // Uppdatera lokal state
      setApplications((prev) =>
        prev.map((app) =>
          app.id === id
            ? {
                ...app,
                toDate: app.extensionRequest.newEndDate,
                contractEndDate: app.extensionRequest.newEndDate,
                extensionRequest: {
                  ...app.extensionRequest,
                  status: 'approved',
                },
              }
            : app
        )
      );

      toast({ type: 'success', message: 'Förlängning godkänd!' });
      setModalOpen(false);
      setSelectedApp(null);
    } catch (err) {
      console.error('Error approving extension:', err);
      toast({ type: 'error', message: 'Kunde inte godkänna förlängning' });
    } finally {
      setExtensionLoading(false);
    }
  };

  // Hantera avslag av förlängningsförfrågan
  const handleRejectExtension = async (id, reason) => {
    setExtensionLoading(true);
    try {
      const rejectExtension = httpsCallable(functions, 'rejectExtensionRequest');
      await rejectExtension({ applicationId: id, reason });

      // Uppdatera lokal state
      setApplications((prev) =>
        prev.map((app) =>
          app.id === id
            ? {
                ...app,
                extensionRequest: {
                  ...app.extensionRequest,
                  status: 'rejected',
                  rejectionReason: reason,
                },
              }
            : app
        )
      );

      toast({ type: 'success', message: 'Förlängning avslagen' });
      setModalOpen(false);
      setSelectedApp(null);
      setExtensionRejectionReason('');
      setShowExtensionRejectionInput(false);
    } catch (err) {
      console.error('Error rejecting extension:', err);
      toast({ type: 'error', message: 'Kunde inte avslå förlängning' });
    } finally {
      setExtensionLoading(false);
    }
  };

  // Hantera initiering av kreditkontroll
  // Beräkna brådskande ansökningar (väntat > 5 dagar)
  const urgentApplications = applications.filter(app => {
    if (app.status !== 'PENDING' && app.status !== 'CREDIT_CHECK_COMPLETE') return false;
    const createdDate = new Date(app.createdAt || app.creditCheckDate);
    const daysSinceCreated = Math.floor((new Date() - createdDate) / (1000 * 60 * 60 * 24));
    return daysSinceCreated >= 5;
  }).slice(0, 5); // Max 5 brådskande

  // Senaste aktivitet (baserat på senast uppdaterad)
  const recentActivity = [...applications]
    .sort((a, b) => {
      const dateA = new Date(a.updatedAt || a.createdAt || 0);
      const dateB = new Date(b.updatedAt || b.createdAt || 0);
      return dateB - dateA;
    })
    .slice(0, 5); // Max 5 senaste

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-8">
      <H1>{t('dashboard.applications.title')}</H1>
      {loading ? (
        <>
          {/* Skeletons för KPI-kort */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white rounded-2xl shadow p-5 flex items-center gap-4 animate-pulse">
                <div className="rounded-full bg-zinc-100 p-3 w-12 h-12" />
                <div>
                  <div className="h-6 w-16 bg-zinc-100 rounded mb-2" />
                  <div className="h-4 w-24 bg-zinc-100 rounded" />
                </div>
              </div>
            ))}
          </div>
          {/* Skeleton för filterrad */}
          <div className="flex gap-4 mb-6 animate-pulse">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-10 w-40 bg-zinc-100 rounded-lg" />
            ))}
          </div>
          {/* Skeleton för tabs */}
          <div className="flex gap-4 mb-6 animate-pulse">
            {[...Array(2)].map((_, i) => (
              <div key={i} className="h-8 w-32 bg-zinc-100 rounded-full" />
            ))}
          </div>
          {/* Skeleton för tabell */}
          <div className="bg-white rounded-2xl shadow p-6 animate-pulse">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-zinc-500">
                    <th className="py-2 pr-4 text-left font-medium">Fastighet</th>
                    <th className="py-2 pr-4 text-left font-medium">Ägare</th>
                    <th className="py-2 pr-4 text-left font-medium">Hyresgäst</th>
                    <th className="py-2 pr-4 text-left font-medium">Datum</th>
                    <th className="py-2 pr-4 text-left font-medium">Status</th>
                    <th className="py-2 pr-4 text-left font-medium">Åtgärder</th>
                  </tr>
                </thead>
                <tbody>
                  {[...Array(4)].map((_, i) => (
                    <tr key={i} className="border-b last:border-b-0">
                      <td className="py-2 pr-4"><div className="h-4 w-24 bg-zinc-100 rounded" /></td>
                      <td className="py-2 pr-4"><div className="h-4 w-20 bg-zinc-100 rounded" /></td>
                      <td className="py-2 pr-4"><div className="h-4 w-20 bg-zinc-100 rounded" /></td>
                      <td className="py-2 pr-4"><div className="h-4 w-24 bg-zinc-100 rounded" /></td>
                      <td className="py-2 pr-4"><div className="h-4 w-16 bg-zinc-100 rounded" /></td>
                      <td className="py-2 pr-4"><div className="h-4 w-12 bg-zinc-100 rounded" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : (
        <>
          {/* Översiktskort */}
          <div className="mb-8">
            <h2 className="text-xl font-bold text-[#222222] mb-4 font-['Poppins']">Översikt</h2>
            <div className={`grid grid-cols-1 gap-4 ${kpi.Extension > 0 ? 'md:grid-cols-4' : 'md:grid-cols-3'}`}>
              {/* Att granska */}
              <div
                className="bg-white rounded-xl shadow-md p-6 cursor-pointer transition-all duration-200 hover:shadow-lg hover:border-2 hover:border-[#62B8E9] group"
                onClick={() => setTab('Pending')}
              >
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-lg font-semibold text-[#626262] group-hover:text-[#62B8E9] transition-colors">
                    Att granska
                  </h3>
                  {kpi.Pending > 0 && (
                    <span className="bg-[#62B8E9] text-white text-xs font-bold px-2 py-1 rounded-full">
                      {kpi.Pending}
                    </span>
                  )}
                </div>
                <div className="text-4xl font-bold text-[#222222] mb-3">{kpi.Pending}</div>
                <button className="w-full bg-[#F9FCFF] border border-[#E2EAF0] text-[#62B8E9] font-semibold py-2 px-4 rounded-lg group-hover:bg-[#62B8E9] group-hover:text-white transition-colors">
                  Se alla →
                </button>
              </div>

              {/* Kreditbeslut */}
              <div
                className="bg-white rounded-xl shadow-md p-6 cursor-pointer transition-all duration-200 hover:shadow-lg hover:border-2 hover:border-[#62B8E9] group"
                onClick={() => setTab('CreditCheckComplete')}
              >
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-lg font-semibold text-[#626262] group-hover:text-[#62B8E9] transition-colors">
                    Kreditbeslut
                  </h3>
                  {kpi.CreditCheckComplete > 0 && (
                    <span className="bg-[#62B8E9] text-white text-xs font-bold px-2 py-1 rounded-full">
                      {kpi.CreditCheckComplete}
                    </span>
                  )}
                </div>
                <div className="text-4xl font-bold text-[#222222] mb-3">{kpi.CreditCheckComplete}</div>
                <button className="w-full bg-[#F9FCFF] border border-[#E2EAF0] text-[#62B8E9] font-semibold py-2 px-4 rounded-lg group-hover:bg-[#62B8E9] group-hover:text-white transition-colors">
                  Se alla →
                </button>
              </div>

              {/* Godkända/Aktiva */}
              <div
                className="bg-white rounded-xl shadow-md p-6 cursor-pointer transition-all duration-200 hover:shadow-lg hover:border-2 hover:border-[#62B8E9] group"
                onClick={() => setTab('Active')}
              >
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-lg font-semibold text-[#626262] group-hover:text-[#62B8E9] transition-colors">
                    Godkända/Aktiva
                  </h3>
                  {kpi.Active > 0 && (
                    <span className="bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                      {kpi.Active}
                    </span>
                  )}
                </div>
                <div className="text-4xl font-bold text-[#222222] mb-3">{kpi.Active}</div>
                <button className="w-full bg-[#F9FCFF] border border-[#E2EAF0] text-[#62B8E9] font-semibold py-2 px-4 rounded-lg group-hover:bg-[#62B8E9] group-hover:text-white transition-colors">
                  Se alla →
                </button>
              </div>

              {/* Förlängningar — bara när något väntar på beslut, annars brus */}
              {kpi.Extension > 0 && (
                <div
                  className="bg-white rounded-xl shadow-md p-6 cursor-pointer transition-all duration-200 hover:shadow-lg hover:border-2 hover:border-[#62B8E9] group"
                  onClick={() => setTab('Extension')}
                >
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-lg font-semibold text-[#626262] group-hover:text-[#62B8E9] transition-colors">
                      Förlängningar
                    </h3>
                    <span className="bg-[#F57F17] text-white text-xs font-bold px-2 py-1 rounded-full">
                      {kpi.Extension}
                    </span>
                  </div>
                  <div className="text-4xl font-bold text-[#222222] mb-3">{kpi.Extension}</div>
                  <button className="w-full bg-[#F9FCFF] border border-[#E2EAF0] text-[#62B8E9] font-semibold py-2 px-4 rounded-lg group-hover:bg-[#62B8E9] group-hover:text-white transition-colors">
                    Se alla →
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Uthyrningsportfölj sektion */}
          <div className="mb-8">
            <h2 className="text-xl font-bold text-[#222222] mb-4 font-['Poppins']">Uthyrningsportfölj</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Godkänd uthyrning */}
              <div className="bg-white rounded-xl shadow-md p-6">
                <div className="flex justify-between items-start mb-3">
                  <h3 className="text-sm font-semibold text-[#626262]">
                    Godkänd uthyrning
                  </h3>
                  {rentalKpi.APPROVED > 0 && (
                    <span className="bg-green-100 text-green-800 text-xs font-bold px-2 py-1 rounded-full">
                      {rentalKpi.APPROVED}
                    </span>
                  )}
                </div>
                <div className="text-3xl font-bold text-[#222222]">{rentalKpi.APPROVED}</div>
                <p className="text-xs text-[#626262] mt-2">Före avtalets startdatum</p>
              </div>

              {/* Aktiv uthyrning */}
              <div className="bg-white rounded-xl shadow-md p-6">
                <div className="flex justify-between items-start mb-3">
                  <h3 className="text-sm font-semibold text-[#626262]">
                    Aktiv uthyrning
                  </h3>
                  {rentalKpi.ACTIVE > 0 && (
                    <span className="bg-blue-100 text-blue-800 text-xs font-bold px-2 py-1 rounded-full">
                      {rentalKpi.ACTIVE}
                    </span>
                  )}
                </div>
                <div className="text-3xl font-bold text-[#222222]">{rentalKpi.ACTIVE}</div>
                <p className="text-xs text-[#626262] mt-2">Pågående hyresperiod</p>
              </div>

              {/* Löper ut snart */}
              <div className="bg-white rounded-xl shadow-md p-6">
                <div className="flex justify-between items-start mb-3">
                  <h3 className="text-sm font-semibold text-[#626262]">
                    Löper ut snart
                  </h3>
                  {rentalKpi.EXPIRING_SOON > 0 && (
                    <span className="bg-orange-100 text-orange-800 text-xs font-bold px-2 py-1 rounded-full">
                      {rentalKpi.EXPIRING_SOON}
                    </span>
                  )}
                </div>
                <div className="text-3xl font-bold text-[#222222]">{rentalKpi.EXPIRING_SOON}</div>
                <p className="text-xs text-[#626262] mt-2">30 dagar kvar</p>
              </div>

              {/* Avslutad uthyrning */}
              <div className="bg-white rounded-xl shadow-md p-6">
                <div className="flex justify-between items-start mb-3">
                  <h3 className="text-sm font-semibold text-[#626262]">
                    Avslutad uthyrning
                  </h3>
                  {rentalKpi.COMPLETED > 0 && (
                    <span className="bg-gray-100 text-gray-800 text-xs font-bold px-2 py-1 rounded-full">
                      {rentalKpi.COMPLETED}
                    </span>
                  )}
                </div>
                <div className="text-3xl font-bold text-[#222222]">{rentalKpi.COMPLETED}</div>
                <p className="text-xs text-[#626262] mt-2">Hyresperiod avslutad</p>
              </div>
            </div>
          </div>

          {/* Brådskande sektion */}
          {urgentApplications.length > 0 && (
            <div className="mb-8">
              <h2 className="text-xl font-bold text-[#222222] mb-4 font-['Poppins'] flex items-center gap-2">
                <AlertCircle className="w-6 h-6 text-[#62B8E9]" />
                Brådskande ({urgentApplications.length})
              </h2>
              <div className="bg-white rounded-xl shadow-md p-6 space-y-3">
                {urgentApplications.map((app) => {
                  const createdDate = new Date(app.createdAt || app.creditCheckDate);
                  const daysSinceCreated = Math.floor((new Date() - createdDate) / (1000 * 60 * 60 * 24));
                  return (
                    <div
                      key={app.id}
                      className="flex items-center justify-between p-4 bg-[#F9FCFF] border border-[#E2EAF0] rounded-lg hover:border-[#62B8E9] transition-colors cursor-pointer"
                      onClick={() => { setSelectedApp(app); setModalOpen(true); }}
                    >
                      <div className="flex-1">
                        <div className="font-semibold text-[#222222]">
                          {app.owner} → {app.tenant || 'Hyresgäst ej angiven'}
                        </div>
                        <div className="text-sm text-[#626262] mt-1">
                          {app.status === 'PENDING' ? 'Väntat' : 'Kreditcheck väntat'} {daysSinceCreated} dagar
                        </div>
                      </div>
                      <button className="bg-[#62B8E9] text-white font-semibold px-4 py-2 rounded-lg hover:bg-[#5ba3d4] transition-colors">
                        Åtgärd
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Senaste aktivitet */}
          <div className="mb-8">
            <h2 className="text-xl font-bold text-[#222222] mb-4 font-['Poppins']">Senaste aktivitet</h2>
            <div className="bg-white rounded-xl shadow-md p-6 space-y-3">
              {recentActivity.length > 0 ? (
                recentActivity.map((app) => {
                  const updatedDate = new Date(app.updatedAt || app.createdAt);
                  const today = new Date();
                  const yesterday = new Date(today);
                  yesterday.setDate(yesterday.getDate() - 1);

                  let timeLabel = '';
                  if (updatedDate.toDateString() === today.toDateString()) {
                    timeLabel = 'Idag';
                  } else if (updatedDate.toDateString() === yesterday.toDateString()) {
                    timeLabel = 'Igår';
                  } else {
                    const daysAgo = Math.floor((today - updatedDate) / (1000 * 60 * 60 * 24));
                    timeLabel = `${daysAgo} ${daysAgo === 1 ? 'dag' : 'dagar'} sedan`;
                  }

                  let activityText = '';
                  if (app.status === 'PENDING') activityText = 'Ansökan mottagen';
                  else if (app.status === 'APPROVED') activityText = 'Godkänd av BRF';
                  else if (app.status === 'CREDIT_CHECK_COMPLETE') activityText = 'Kreditcheck uppladdad';
                  else if (app.status === 'FINAL_APPROVED') activityText = rentalPhaseLabel(app)?.text || 'Hyresgäst godkänd';
                  else if (app.status === 'REJECTED' || app.status === 'FINAL_REJECTED') activityText = 'Avvisad';
                  else activityText = 'Uppdaterad';

                  return (
                    <div
                      key={app.id}
                      className="flex items-center gap-4 p-3 hover:bg-[#F9FCFF] rounded-lg cursor-pointer transition-colors"
                      onClick={() => { setSelectedApp(app); setModalOpen(true); }}
                    >
                      <div className="text-sm font-semibold text-[#626262] min-w-[100px]">
                        ⏱️ {timeLabel}
                      </div>
                      <div className="flex-1 text-[#222222]">
                        {app.owner} | {activityText}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center text-[#626262] py-4">Ingen aktivitet ännu</div>
              )}
            </div>
          </div>

          {/* Alla ansökningar tabell */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-[#222222] font-['Poppins']">
                Alla ansökningar ({tab !== 'All' ? `${filtered.length} av ${visibleApplications.length}` : visibleApplications.length})
              </h2>
              <div className="flex gap-2 items-center">
                <label className="flex items-center gap-2 text-sm text-[#626262] cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={showArchived}
                    onChange={(e) => setShowArchived(e.target.checked)}
                    className="rounded border-[#E2EAF0]"
                  />
                  Visa arkiverade
                </label>
                <select
                  value={tab}
                  onChange={e => setTab(e.target.value)}
                  className="bg-white border border-[#E2EAF0] rounded-lg px-4 py-2 text-[#626262] font-semibold focus:outline-none focus:ring-2 focus:ring-[#62B8E9]"
                >
                  <option value="All">Alla</option>
                  <option value="Pending">Att granska</option>
                  <option value="CreditCheckComplete">Kreditbeslut</option>
                  <option value="Extension">Förlängningar</option>
                  <option value="Active">Godkända/Aktiva</option>
                  <option value="Rejected">Avvisade</option>
                  <option value="Historical">Historik</option>
                </select>
              </div>
            </div>

          <div className="overflow-x-auto bg-white rounded-2xl shadow-md border border-[#E2EAF0]">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-zinc-500">
                  <th className="px-4 py-3 text-left font-medium">
                    {t('dashboard.applications.table.property')}
                  </th>
                  <th className="px-4 py-3 text-left font-medium">
                    {t('dashboard.applications.table.owner')}
                  </th>
                  <th className="px-4 py-3 text-left font-medium">
                    {t('dashboard.applications.table.tenant')}
                  </th>
                  <th className="px-4 py-3 text-left font-medium">
                    {t('dashboard.applications.table.dates')}
                  </th>
                  <th className="px-4 py-3 text-left font-medium">
                    Skäl
                  </th>
                  <th className="px-4 py-3 text-left font-medium">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left font-medium">
                    {t('dashboard.applications.table.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#e6f3fa]">
                {filtered.map(app => (
                  <tr 
                    key={app.id} 
                    className="hover:bg-[#f3faff] transition cursor-pointer group"
                    onClick={() => { setSelectedApp(app); setModalOpen(true); }}
                  >
                    <td className="px-4 py-3 text-zinc-800">{app.property}</td>
                    <td className="px-4 py-3 text-zinc-600">{app.owner}</td>
                    <td className="px-4 py-3 text-zinc-600">{app.tenant}</td>
                    <td className="px-4 py-3 text-zinc-600">
                      {app.fromDate && app.toDate 
                        ? `${new Date(app.fromDate).toLocaleDateString('sv-SE')} - ${new Date(app.toDate).toLocaleDateString('sv-SE')}`
                        : app.from && app.to 
                          ? `${new Date(app.from).toLocaleDateString('sv-SE')} - ${new Date(app.to).toLocaleDateString('sv-SE')}`
                          : 'Ej angivet'
                      }
                    </td>
                    <td className="px-4 py-3 text-zinc-600">
                      {app.reason || 'Ej angivet'}
                    </td>
                    <td className="px-4 py-3">
                      {getApplicationStatusBadge(app)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        className="p-1.5 rounded-full hover:bg-zinc-100 transition text-zinc-400 opacity-0 group-hover:opacity-100"
                        title="Visa detaljer"
                        aria-label="Visa detaljer"
                        onClick={(e) => { 
                          e.stopPropagation(); // Förhindra att raden också triggas
                          setSelectedApp(app); 
                          setModalOpen(true); 
                        }}
                      >
                        <MoreHorizontal className="w-5 h-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </div>

          <Dialog open={modalOpen} onClose={() => { setModalOpen(false); setRejectionReason(''); setShowRejectionInput(false); setShowMoreDetails(false); setModalTab('details'); }} className="fixed z-50 inset-0 flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
            <div className="relative bg-white rounded-2xl shadow-lg w-full max-w-2xl mx-auto max-h-[90vh] flex flex-col">
              <button
                className="absolute top-4 right-4 p-2 rounded-full hover:bg-zinc-100 text-zinc-500 z-10"
                onClick={() => { setModalOpen(false); setRejectionReason(''); setShowRejectionInput(false); setShowMoreDetails(false); setModalTab('details'); }}
                aria-label="Stäng"
              >
                <X className="w-5 h-5" />
              </button>
              {selectedApp && (
                <>
                {/* Header + Tabs */}
                <div className="p-6 pb-0">
                  {/* Kompakt header */}
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-bold text-[#222222] font-['Poppins']">
                      {selectedApp.status === 'FINAL_APPROVED' && (
                        <span className="inline-block mr-2 text-green-500">✓</span>
                      )}
                      {selectedApp.status === 'CREDIT_CHECK_COMPLETE' && selectedApp.creditCheckAnswers && (
                        <span className={`inline-block mr-2 ${
                          (selectedApp.creditCheckAnswers.betalningsanmarkningar === 'nej' &&
                           selectedApp.creditCheckAnswers.skuldsaldo === 'nej' &&
                           selectedApp.creditCheckAnswers.inkomst === 'ja')
                            ? 'text-green-500'
                            : 'text-red-500'
                        }`}>
                          {(selectedApp.creditCheckAnswers.betalningsanmarkningar === 'nej' &&
                            selectedApp.creditCheckAnswers.skuldsaldo === 'nej' &&
                            selectedApp.creditCheckAnswers.inkomst === 'ja')
                            ? '✓'
                            : '🔴'}
                        </span>
                      )}
                      {selectedApp.owner} → {selectedApp.tenant || 'Hyresgäst ej angiven'}
                    </h2>
                    {selectedApp.status === 'FINAL_APPROVED' && (
                      <span className="px-3 py-1 rounded-full text-xs font-bold bg-green-100 text-green-700">
                        Godkänd uthyrning
                      </span>
                    )}
                  </div>

                  {/* Tab bar */}
                  <div className="flex border-b border-[#E2EAF0] mt-4">
                    <button
                      onClick={() => setModalTab('details')}
                      className={`px-4 py-3 text-sm font-semibold border-b-2 transition-colors ${
                        modalTab === 'details'
                          ? 'border-[#62B8E9] text-[#62B8E9]'
                          : 'border-transparent text-[#626262] hover:text-[#62B8E9]'
                      }`}
                    >
                      Detaljer
                    </button>
                    <ChatToggleButton
                      active={modalTab === 'chat'}
                      unreadCount={selectedApp.chatUnread?.[auth.currentUser?.uid] || 0}
                      onClick={() => setModalTab('chat')}
                    />
                  </div>
                </div>

                {/* Tab content */}
                {modalTab === 'chat' ? (
                  <ChatPanel
                    applicationId={selectedApp.id}
                    currentUser={{
                      uid: auth.currentUser?.uid || '',
                      name: currentUserName || 'BRF Admin',
                      role: 'brfadmin',
                    }}
                  />
                ) : (
                <div className="overflow-y-auto p-6 space-y-4">
                  {/* Info och kreditresultat */}
                  <div className="space-y-2">
                    <p className="text-sm text-[#626262]">
                      📍 {selectedApp.address || selectedApp.property} {selectedApp.apartmentNumber && `Lgh ${selectedApp.apartmentNumber}`}
                    </p>
                    <p className="text-sm text-[#626262]">
                      📅 {(() => {
                        const fromDate = selectedApp.fromDate || selectedApp.from;
                        const toDate = selectedApp.toDate || selectedApp.to;
                        if (!fromDate || !toDate) return 'Ej angivet';
                        return `${new Date(fromDate).toLocaleDateString('sv-SE')} – ${new Date(toDate).toLocaleDateString('sv-SE')}`;
                      })()}
                    </p>
                    <p className="text-sm text-[#626262]">
                      📝 {selectedApp.reason || 'Ej angivet'}
                    </p>

                    {/* Inline kreditresultat */}
                    {selectedApp.status === 'CREDIT_CHECK_COMPLETE' && selectedApp.creditCheckAnswers && (
                      <div className="flex flex-wrap gap-2 pt-2">
                        <span className={`px-3 py-1.5 rounded-full text-xs font-bold ${
                          selectedApp.creditCheckAnswers.betalningsanmarkningar === 'ja'
                            ? 'bg-red-500 text-white'
                            : 'bg-green-500 text-white'
                        }`}>
                          Betalningsanm: {selectedApp.creditCheckAnswers.betalningsanmarkningar === 'ja' ? 'JA' : 'NEJ'}
                        </span>
                        <span className={`px-3 py-1.5 rounded-full text-xs font-bold ${
                          selectedApp.creditCheckAnswers.skuldsaldo === 'ja'
                            ? 'bg-red-500 text-white'
                            : 'bg-green-500 text-white'
                        }`}>
                          Kronofogden: {selectedApp.creditCheckAnswers.skuldsaldo === 'ja' ? 'JA' : 'NEJ'}
                        </span>
                        <span className={`px-3 py-1.5 rounded-full text-xs font-bold ${
                          selectedApp.creditCheckAnswers.inkomst === 'ja'
                            ? 'bg-green-500 text-white'
                            : 'bg-red-500 text-white'
                        }`}>
                          Inkomst OK: {selectedApp.creditCheckAnswers.inkomst === 'ja' ? 'JA' : 'NEJ'}
                        </span>
                        {selectedApp.creditCheckPdfUrl && (
                          <button
                            onClick={() => handlePdfDownload(selectedApp.creditCheckPdfUrl)}
                            disabled={pdfDownloadLoading}
                            className="px-3 py-1.5 rounded-full text-xs font-bold bg-[#62B8E9] text-white hover:bg-[#5ba3d4] transition-colors disabled:opacity-50"
                          >
                            📄 Visa PDF
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Bilagor (read-only för BRF-admin) */}
                  <AttachmentsSection
                    applicationId={selectedApp.id}
                    attachments={selectedApp.attachments || []}
                    canUpload={false}
                  />

                  {/* Förlängningsförfrågan - Visa om pending */}
                  {selectedApp.extensionRequest?.status === 'pending' && (
                    <div className="border-t border-[#E2EAF0] pt-4 mt-4">
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                        <div className="flex items-start gap-3">
                          <div className="flex-1">
                            <p className="text-blue-900 font-semibold text-sm mb-1">
                              Förlängningsförfrågan
                            </p>
                            <p className="text-blue-800 text-sm mb-2">
                              Ägaren har begärt att förlänga uthyrningen till:{' '}
                              <strong>{new Date(selectedApp.extensionRequest.newEndDate).toLocaleDateString('sv-SE')}</strong>
                            </p>
                            <p className="text-blue-700 text-xs">
                              Begärd: {new Date(selectedApp.extensionRequest.requestedDate?.seconds * 1000 || selectedApp.extensionRequest.requestedDate).toLocaleDateString('sv-SE')}
                            </p>
                          </div>
                        </div>
                      </div>

                      {!showExtensionRejectionInput ? (
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleApproveExtension(selectedApp.id)}
                            disabled={extensionLoading}
                            className="flex-1 text-white font-bold py-2.5 px-4 rounded-lg transition-colors disabled:opacity-50 text-sm"
                            style={{ backgroundColor: '#62B8E9' }}
                            onMouseEnter={(e) => !extensionLoading && (e.target.style.backgroundColor = '#4A9FD1')}
                            onMouseLeave={(e) => e.target.style.backgroundColor = '#62B8E9'}
                          >
                            {extensionLoading ? 'Godkänner...' : 'Godkänn förlängning'}
                          </button>
                          <button
                            onClick={() => setShowExtensionRejectionInput(true)}
                            disabled={extensionLoading}
                            className="flex-1 text-white font-bold py-2.5 px-4 rounded-lg transition-colors disabled:opacity-50 text-sm"
                            style={{ backgroundColor: '#62B8E9' }}
                            onMouseEnter={(e) => !extensionLoading && (e.target.style.backgroundColor = '#4A9FD1')}
                            onMouseLeave={(e) => e.target.style.backgroundColor = '#62B8E9'}
                          >
                            Avslå förlängning
                          </button>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <input
                            type="text"
                            placeholder="Ange skäl vid avslag (obligatoriskt)"
                            value={extensionRejectionReason}
                            onChange={e => setExtensionRejectionReason(e.target.value)}
                            className="w-full bg-white border border-[#E2EAF0] rounded-lg px-3 py-2 text-[#222222] text-sm focus:outline-none focus:ring-2 focus:ring-[#62B8E9]"
                            disabled={extensionLoading}
                            autoFocus
                          />
                          <div className="flex gap-2">
                            <button
                              className="flex-1 border border-[#E2EAF0] text-[#626262] rounded-lg py-2 font-semibold hover:bg-[#F9FCFF] transition-colors text-sm"
                              onClick={() => {
                                setShowExtensionRejectionInput(false);
                                setExtensionRejectionReason('');
                              }}
                              disabled={extensionLoading}
                            >
                              Avbryt
                            </button>
                            <button
                              className="flex-1 text-white rounded-lg py-2 font-bold transition-colors text-sm disabled:opacity-50"
                              style={{ backgroundColor: '#62B8E9' }}
                              onMouseEnter={(e) => !extensionLoading && (e.target.style.backgroundColor = '#4A9FD1')}
                              onMouseLeave={(e) => e.target.style.backgroundColor = '#62B8E9'}
                              onClick={() => handleRejectExtension(selectedApp.id, extensionRejectionReason)}
                              disabled={extensionLoading || !extensionRejectionReason.trim()}
                            >
                              {extensionLoading ? 'Avslår...' : 'Bekräfta'}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Avslag. Skälet styrelsen skrev syntes bara för ägaren —
                      föreningen såg en nekad ansökan utan att kunna se varför. */}
                  {(selectedApp.status === 'REJECTED' || selectedApp.status === 'FINAL_REJECTED') && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-4">
                      <p className="text-sm font-semibold text-red-900">
                        {selectedApp.status === 'FINAL_REJECTED' ? 'Slutgiltigt avslag' : 'Avslagen i första granskningen'}
                      </p>
                      {(selectedApp.finalRejectionReason || selectedApp.rejectionReason) ? (
                        <p className="text-sm text-red-800 mt-1 whitespace-pre-line">
                          {selectedApp.finalRejectionReason || selectedApp.rejectionReason}
                        </p>
                      ) : (
                        <p className="text-sm text-red-800 mt-1">Inget skäl angavs.</p>
                      )}
                      <p className="text-xs text-red-700 mt-2">
                        {[
                          formatMoment(
                            selectedApp.status === 'FINAL_REJECTED'
                              ? (selectedApp.creditCheck?.rejectedAt || selectedApp.finalApprovedAt)
                              : selectedApp.reviewedAt
                          ),
                          selectedApp.status === 'FINAL_REJECTED'
                            ? (selectedApp.finalDecisionByName || selectedApp.finalApprovedBy)
                            : (selectedApp.reviewedByName || selectedApp.reviewedBy),
                        ].filter(Boolean).join(' · ')}
                      </p>
                    </div>
                  )}

                  {/* Beslutsknappar - Kompakt version.
                      En avslagen ansökan (steg 1) passerade förut det här villkoret
                      och fick Godkänn/Avslå igen. Servern vet inte att REJECTED →
                      APPROVED är förbjudet — det kommer med tillståndsmaskinen. */}
                  {selectedApp.status !== 'REJECTED' && selectedApp.status !== 'FINAL_APPROVED' && selectedApp.status !== 'FINAL_REJECTED' && selectedApp.extensionRequest?.status !== 'pending' && (
                    <div className="border-t border-[#E2EAF0] pt-4 mt-4">
                      {selectedApp.status === 'CREDIT_CHECK_COMPLETE' ? (
                      <div className="space-y-2">
                        <input
                          type="text"
                          placeholder="Ange skäl vid avslag (valfritt)"
                          value={finalRejectionReason}
                          onChange={e => setFinalRejectionReason(e.target.value)}
                          className="w-full bg-white border border-[#E2EAF0] rounded-lg px-3 py-2 text-[#222222] text-sm focus:outline-none focus:ring-2 focus:ring-[#62B8E9]"
                          disabled={finalDecisionLoading}
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleFinalDecision(selectedApp.id, 'AWAITING_OWNER_DECISION')}
                            disabled={finalDecisionLoading}
                            className="flex-1 bg-green-500 text-white font-bold py-2.5 px-4 rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50 text-sm"
                          >
                            ✓ Godkänn
                          </button>
                          <button
                            onClick={() => handleFinalDecision(selectedApp.id, 'FINAL_REJECTED', finalRejectionReason)}
                            disabled={finalDecisionLoading || !finalRejectionReason}
                            className="flex-1 bg-red-500 text-white font-bold py-2.5 px-4 rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 text-sm"
                          >
                            ✗ Avslå
                          </button>
                        </div>
                      </div>
                    ) : selectedApp.status === 'TENANT_INFO_SUBMITTED' ? (
                      <div className="space-y-3">
                        {/* Visa status för kreditkontroll */}
                        {selectedApp.creditCheck?.status === 'consent_pending' ? (
                          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                            <div className="flex items-start gap-3">
                              <div className="text-yellow-600 text-xl">⏳</div>
                              <div>
                                <p className="text-yellow-900 font-semibold text-sm mb-1">
                                  Väntar på hyresgästens samtycke
                                </p>
                                <p className="text-yellow-800 text-sm">
                                  Ett mail har skickats till hyresgästen för att ge sitt samtycke till kreditupplysning.
                                </p>
                              </div>
                            </div>
                          </div>
                        ) : selectedApp.creditCheck?.status === 'consent_given' ? (
                          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                            <div className="flex items-start gap-3">
                              <div className="text-green-600 text-xl">✅</div>
                              <div>
                                <p className="text-green-900 font-semibold text-sm mb-1">
                                  Samtycke mottaget
                                </p>
                                <p className="text-green-800 text-sm">
                                  Hyresgästen har gett sitt samtycke. Hyria kommer nu att utföra kreditkontrollen.
                                </p>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                            <div className="flex items-start gap-3">
                              <div className="text-amber-600 text-xl">⏳</div>
                              <div className="flex-1">
                                <p className="text-amber-900 font-semibold text-sm mb-1">
                                  Väntar på Hyria
                                </p>
                                <p className="text-amber-800 text-sm">
                                  Hyria har fått notifiering om att godkänna hyresgästen och initiera kreditkontroll.
                                  Du kommer att få besked när kreditkontrollen är klar.
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      !showRejectionInput ? (
                        <div className="flex gap-2">
                          <button
                            className="flex-1 bg-green-500 text-white font-bold py-2.5 px-4 rounded-lg hover:bg-green-600 transition-colors text-sm"
                            onClick={() => handleStatusChange(selectedApp.id, 'Approved')}
                            disabled={loadingId === selectedApp.id + 'APPROVED'}
                          >
                            ✓ Godkänn
                          </button>
                          <button
                            className="flex-1 bg-red-500 text-white font-bold py-2.5 px-4 rounded-lg hover:bg-red-600 transition-colors text-sm"
                            onClick={() => setShowRejectionInput(true)}
                            disabled={loadingId === selectedApp.id + 'REJECTED'}
                          >
                            ✗ Avslå
                          </button>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <input
                            type="text"
                            placeholder="Ange skäl vid avslag (obligatoriskt)"
                            value={rejectionReason}
                            onChange={e => setRejectionReason(e.target.value)}
                            className="w-full bg-white border border-[#E2EAF0] rounded-lg px-3 py-2 text-[#222222] text-sm focus:outline-none focus:ring-2 focus:ring-[#62B8E9]"
                            disabled={loadingId === selectedApp.id + 'REJECTED'}
                            autoFocus
                          />
                          <div className="flex gap-2">
                            <button
                              className="flex-1 border border-[#E2EAF0] text-[#626262] rounded-lg py-2 font-semibold hover:bg-[#F9FCFF] transition-colors text-sm"
                              onClick={() => {
                                setShowRejectionInput(false);
                                setRejectionReason('');
                              }}
                              disabled={loadingId === selectedApp.id + 'REJECTED'}
                            >
                              Avbryt
                            </button>
                            <button
                              className="flex-1 bg-red-500 text-white rounded-lg py-2 font-bold hover:bg-red-600 transition-colors text-sm"
                              onClick={() => handleStatusChange(selectedApp.id, 'Rejected', rejectionReason)}
                              disabled={loadingId === selectedApp.id + 'REJECTED' || !rejectionReason.trim()}
                            >
                              Bekräfta
                            </button>
                          </div>
                        </div>
                      )
                    )}
                    </div>
                  )}

                  {/* Komplett information för godkända ansökningar */}
                  {selectedApp.status === 'FINAL_APPROVED' && (
                    <div className="border-t border-[#E2EAF0] pt-3 mt-3 space-y-3">
                      {/* Hyresgäst */}
                      <div>
                        <h4 className="text-xs font-bold text-[#222222] mb-1.5">Hyresgäst</h4>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                          <div className="flex items-baseline gap-1">
                            <span className="text-xs text-[#626262]">Namn:</span>
                            <span className="text-xs text-[#222222] font-medium">
                              {selectedApp.tenantFirstName && selectedApp.tenantLastName
                                ? `${selectedApp.tenantFirstName} ${selectedApp.tenantLastName}`
                                : 'Ej angivet'}
                            </span>
                          </div>
                          <div className="flex items-baseline gap-1">
                            <span className="text-xs text-[#626262]">Personnr:</span>
                            <span className="text-xs text-[#222222] font-medium">{selectedApp.tenantPersonalNumber || 'Ej angivet'}</span>
                          </div>
                          <div className="flex items-baseline gap-1">
                            <span className="text-xs text-[#626262]">Telefon:</span>
                            <span className="text-xs text-[#222222] font-medium">{selectedApp.tenantPhoneNumber || 'Ej angivet'}</span>
                          </div>
                          <div className="flex items-baseline gap-1">
                            <span className="text-xs text-[#626262]">E-post:</span>
                            <span className="text-xs text-[#222222] font-medium">{selectedApp.tenantEmail || 'Ej angivet'}</span>
                          </div>
                        </div>
                      </div>

                      {/* Lägenhet */}
                      <div className="border-t border-[#E2EAF0] pt-2">
                        <h4 className="text-xs font-bold text-[#222222] mb-1.5">Lägenhet</h4>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                          <div className="flex items-baseline gap-1">
                            <span className="text-xs text-[#626262]">BRF:</span>
                            <span className="text-xs text-[#222222] font-medium">{selectedApp.housingCooperative || 'Ej angivet'}</span>
                          </div>
                          <div className="flex items-baseline gap-1">
                            <span className="text-xs text-[#626262]">Lgh:</span>
                            <span className="text-xs text-[#222222] font-medium">{selectedApp.apartmentNumber || 'Ej angivet'}</span>
                          </div>
                          <div className="flex items-baseline gap-1">
                            <span className="text-xs text-[#626262]">Adress:</span>
                            <span className="text-xs text-[#222222] font-medium">{selectedApp.address || 'Ej angivet'}</span>
                          </div>
                          <div className="flex items-baseline gap-1">
                            <span className="text-xs text-[#626262]">Yta:</span>
                            <span className="text-xs text-[#222222] font-medium">
                              {selectedApp.rooms || '?'} rum, {selectedApp.squareMeter ? `${selectedApp.squareMeter} m²` : '?'}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Hyrestid & Ekonomi */}
                      <div className="border-t border-[#E2EAF0] pt-2">
                        <h4 className="text-xs font-bold text-[#222222] mb-1.5">Hyrestid & Ekonomi</h4>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                          <div className="flex items-baseline gap-1">
                            <span className="text-xs text-[#626262]">Period:</span>
                            <span className="text-xs text-[#222222] font-medium">
                              {(() => {
                                const fromDate = selectedApp.contractStartDate || selectedApp.fromDate || selectedApp.from;
                                const toDate = selectedApp.contractEndDate || selectedApp.toDate || selectedApp.to;
                                if (!fromDate || !toDate) return 'Ej angivet';
                                return `${new Date(fromDate).toLocaleDateString('sv-SE')} – ${new Date(toDate).toLocaleDateString('sv-SE')}`;
                              })()}
                            </span>
                          </div>
                          <div className="flex items-baseline gap-1">
                            <span className="text-xs text-[#626262]">Hyra:</span>
                            <span className="text-xs text-[#222222] font-medium">{selectedApp.rentalFee ? `${selectedApp.rentalFee} kr/mån` : 'Ej angivet'}</span>
                          </div>
                          <div className="flex items-baseline gap-1">
                            <span className="text-xs text-[#626262]">Deposition:</span>
                            <span className="text-xs text-[#222222] font-medium">{selectedApp.deposit ? `${selectedApp.deposit} kr` : 'Ej angivet'}</span>
                          </div>
                          <div className="flex items-baseline gap-1">
                            <span className="text-xs text-[#626262]">Konto:</span>
                            <span className="text-xs text-[#222222] font-medium">
                              {selectedApp.clearingnummer && selectedApp.bankAccountNumber
                                ? `${selectedApp.clearingnummer}-${selectedApp.bankAccountNumber}`
                                : 'Ej angivet'}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Vad ingår */}
                      {(selectedApp.heatingIncluded || selectedApp.hotWaterIncluded || selectedApp.electricityIncluded ||
                        selectedApp.internetIncluded || selectedApp.parkingIncluded || selectedApp.furnitureIncluded) && (
                        <div className="border-t border-[#E2EAF0] pt-2">
                          <h4 className="text-xs font-bold text-[#222222] mb-1.5">Vad ingår i hyran</h4>
                          <div className="flex flex-wrap gap-1.5">
                            {selectedApp.heatingIncluded && (
                              <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs font-medium">
                                Uppvärmning
                              </span>
                            )}
                            {selectedApp.hotWaterIncluded && (
                              <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs font-medium">
                                Varmvatten
                              </span>
                            )}
                            {selectedApp.electricityIncluded && (
                              <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs font-medium">
                                Hushållsel
                              </span>
                            )}
                            {selectedApp.internetIncluded && (
                              <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs font-medium">
                                Internet
                              </span>
                            )}
                            {selectedApp.parkingIncluded && (
                              <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs font-medium">
                                Parkering
                              </span>
                            )}
                            {selectedApp.furnitureIncluded && (
                              <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs font-medium">
                                Möbler & inventarier
                              </span>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Kreditkontroll */}
                      {selectedApp.creditCheckAnswers && (
                        <div className="border-t border-[#E2EAF0] pt-2">
                          <h4 className="text-xs font-bold text-[#222222] mb-1.5">Kreditkontroll</h4>
                          <div className="flex flex-wrap gap-1.5">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                              selectedApp.creditCheckAnswers.betalningsanmarkningar === 'ja'
                                ? 'bg-red-500 text-white'
                                : 'bg-green-500 text-white'
                            }`}>
                              Betalningsanm: {selectedApp.creditCheckAnswers.betalningsanmarkningar === 'ja' ? 'JA' : 'NEJ'}
                            </span>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                              selectedApp.creditCheckAnswers.skuldsaldo === 'ja'
                                ? 'bg-red-500 text-white'
                                : 'bg-green-500 text-white'
                            }`}>
                              Kronofogden: {selectedApp.creditCheckAnswers.skuldsaldo === 'ja' ? 'JA' : 'NEJ'}
                            </span>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                              selectedApp.creditCheckAnswers.inkomst === 'ja'
                                ? 'bg-green-500 text-white'
                                : 'bg-red-500 text-white'
                            }`}>
                              Inkomst OK: {selectedApp.creditCheckAnswers.inkomst === 'ja' ? 'JA' : 'NEJ'}
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Godkännandeinformation */}
                      {selectedApp.finalApprovedAt && (
                        <div className="border-t border-[#E2EAF0] pt-2">
                          <p className="text-xs text-[#626262]">
                            Godkänd: {new Date(selectedApp.finalApprovedAt).toLocaleDateString('sv-SE')} av {selectedApp.finalApprovedBy || 'BRF admin'}
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Detaljerad information - alltid synlig för ansökningar som inte är FINAL_APPROVED */}
                  {selectedApp.status !== 'FINAL_APPROVED' && (
                    <div className="border-t border-[#E2EAF0] pt-3 mt-3 space-y-3">
                      {/* Hyresgäst - visa om finns */}
                      {(selectedApp.tenantFirstName || selectedApp.tenantLastName || selectedApp.tenantEmail) && (
                        <div>
                          <h4 className="text-xs font-bold text-[#222222] mb-1.5">Hyresgäst</h4>
                          <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                            {(selectedApp.tenantFirstName || selectedApp.tenantLastName) && (
                              <div className="flex items-baseline gap-1">
                                <span className="text-xs text-[#626262]">Namn:</span>
                                <span className="text-xs text-[#222222] font-medium">
                                  {`${selectedApp.tenantFirstName || ''} ${selectedApp.tenantLastName || ''}`.trim() || 'Ej angivet'}
                                </span>
                              </div>
                            )}
                            {selectedApp.tenantPersonalNumber && (
                              <div className="flex items-baseline gap-1">
                                <span className="text-xs text-[#626262]">Personnr:</span>
                                <span className="text-xs text-[#222222] font-medium">{selectedApp.tenantPersonalNumber}</span>
                              </div>
                            )}
                            {selectedApp.tenantPhoneNumber && (
                              <div className="flex items-baseline gap-1">
                                <span className="text-xs text-[#626262]">Telefon:</span>
                                <span className="text-xs text-[#222222] font-medium">{selectedApp.tenantPhoneNumber}</span>
                              </div>
                            )}
                            {selectedApp.tenantEmail && (
                              <div className="flex items-baseline gap-1">
                                <span className="text-xs text-[#626262]">E-post:</span>
                                <span className="text-xs text-[#222222] font-medium">{selectedApp.tenantEmail}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Lägenhet */}
                      <div className={selectedApp.tenantFirstName ? 'border-t border-[#E2EAF0] pt-2' : ''}>
                        <h4 className="text-xs font-bold text-[#222222] mb-1.5">Lägenhet</h4>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                          <div className="flex items-baseline gap-1">
                            <span className="text-xs text-[#626262]">BRF:</span>
                            <span className="text-xs text-[#222222] font-medium">{selectedApp.housingCooperative || 'Ej angivet'}</span>
                          </div>
                          <div className="flex items-baseline gap-1">
                            <span className="text-xs text-[#626262]">Lgh:</span>
                            <span className="text-xs text-[#222222] font-medium">{selectedApp.apartmentNumber || 'Ej angivet'}</span>
                          </div>
                          <div className="flex items-baseline gap-1">
                            <span className="text-xs text-[#626262]">Adress:</span>
                            <span className="text-xs text-[#222222] font-medium">{selectedApp.address || 'Ej angivet'}</span>
                          </div>
                          <div className="flex items-baseline gap-1">
                            <span className="text-xs text-[#626262]">Yta:</span>
                            <span className="text-xs text-[#222222] font-medium">
                              {selectedApp.rooms || '?'} rum, {selectedApp.squareMeter ? `${selectedApp.squareMeter} m²` : '?'}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Ekonomi - visa om finns */}
                      {(selectedApp.rentalFee || selectedApp.deposit) && (
                        <div className="border-t border-[#E2EAF0] pt-2">
                          <h4 className="text-xs font-bold text-[#222222] mb-1.5">Ekonomi</h4>
                          <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                            {selectedApp.rentalFee && (
                              <div className="flex items-baseline gap-1">
                                <span className="text-xs text-[#626262]">Hyra:</span>
                                <span className="text-xs text-[#222222] font-medium">{selectedApp.rentalFee} kr/mån</span>
                              </div>
                            )}
                            {selectedApp.deposit && (
                              <div className="flex items-baseline gap-1">
                                <span className="text-xs text-[#626262]">Deposition:</span>
                                <span className="text-xs text-[#222222] font-medium">{selectedApp.deposit} kr</span>
                              </div>
                            )}
                            {(selectedApp.clearingnummer || selectedApp.bankAccountNumber) && (
                              <div className="flex items-baseline gap-1">
                                <span className="text-xs text-[#626262]">Konto:</span>
                                <span className="text-xs text-[#222222] font-medium">
                                  {selectedApp.clearingnummer && selectedApp.bankAccountNumber
                                    ? `${selectedApp.clearingnummer}-${selectedApp.bankAccountNumber}`
                                    : 'Ej angivet'}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Vad ingår */}
                      {(selectedApp.heatingIncluded || selectedApp.hotWaterIncluded || selectedApp.electricityIncluded ||
                        selectedApp.internetIncluded || selectedApp.parkingIncluded || selectedApp.furnitureIncluded) && (
                        <div className="border-t border-[#E2EAF0] pt-2">
                          <h4 className="text-xs font-bold text-[#222222] mb-1.5">Vad ingår i hyran</h4>
                          <div className="flex flex-wrap gap-1.5">
                            {selectedApp.heatingIncluded && (
                              <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs font-medium">
                                Uppvärmning
                              </span>
                            )}
                            {selectedApp.hotWaterIncluded && (
                              <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs font-medium">
                                Varmvatten
                              </span>
                            )}
                            {selectedApp.electricityIncluded && (
                              <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs font-medium">
                                Hushållsel
                              </span>
                            )}
                            {selectedApp.internetIncluded && (
                              <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs font-medium">
                                Internet
                              </span>
                            )}
                            {selectedApp.parkingIncluded && (
                              <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs font-medium">
                                Parkering
                              </span>
                            )}
                            {selectedApp.furnitureIncluded && (
                              <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs font-medium">
                                Möbler & inventarier
                              </span>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Kommentarer */}
                      {(selectedApp.creditCheckComments || selectedApp.miscellaneousNotes) && (
                        <div className="border-t border-[#E2EAF0] pt-2 space-y-1.5">
                          {selectedApp.creditCheckComments && (
                            <div className="text-xs">
                              <span className="font-semibold text-[#222222]">Kommentar:</span>
                              <p className="text-[#626262] mt-0.5">{selectedApp.creditCheckComments}</p>
                            </div>
                          )}
                          {selectedApp.miscellaneousNotes && (
                            <div className="text-xs">
                              <span className="font-semibold text-[#222222]">Övrigt:</span>
                              <p className="text-[#626262] mt-0.5">{selectedApp.miscellaneousNotes}</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                )}
                </>
              )}
            </div>
          </Dialog>
        </>
      )}
    </div>
  );
}
